import React, { lazy } from "react";
import { Box, Button, Typography } from "@mui/material";
import { Switch, Redirect, Route } from "react-router";

import {RoleName, useSession} from "../lib/session";
import getUserRoles from "../lib/rbac";
import routes, { adminRoutes, viewerRoutes, monitorRoutes } from "./routes";
import Loader, {Spinner} from "../components/Loader";

import { useProjectPlanPermissionQuery } from "../newGraphql/generated/Project.generated";

const NotFound = lazy(() => import("../pages/NotFound"));

function PermissionState({
  title,
  message,
  action,
}: {
  title: string;
  message: string;
  action?: JSX.Element;
}): JSX.Element {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 3,
        bgcolor: "#141414",
      }}
    >
      <Box sx={{ maxWidth: 480, textAlign: "center" }}>
        <Typography variant="h5" sx={{ color: "#f5f5f5", mb: 1 }}>
          {title}
        </Typography>
        <Typography sx={{ color: "#a3a3a3", mb: action ? 3 : 0 }}>
          {message}
        </Typography>
        {action}
      </Box>
    </Box>
  );
}

export default function Content(): JSX.Element {
  const { activeProject } = useSession();
  const userRoles: RoleName[] = getUserRoles(activeProject);
  const isViewer = userRoles.includes(RoleName.VIEWER);
  const isMonitor = userRoles.includes(RoleName.MONITOR);

  const { data, loading, error, refetch  } = useProjectPlanPermissionQuery({
    variables: {
      projectId: activeProject.id
    }
  });

  const projectPermissions: string[] = data?.projectPlanPermission?.appPermissions?.map(t => t.name) ?? [];

  const allowedRoutes = routes.filter( (route) => !route.permission || projectPermissions.includes(route.permission));
  const allowedNormalRoutes = allowedRoutes.filter((obj) => {
    return obj.allowedRoles.some((r) => userRoles.indexOf(r) >= 0);
  });

  const allowedRoutesPerm = adminRoutes.filter( (route) => !route.permission || projectPermissions.includes(route.permission));
  const allowedAdminRoutes = allowedRoutesPerm.filter((obj) => {
    return obj.allowedRoles.some((r) => userRoles.indexOf(r) >= 0);
  });

  // TODO: error when switching projects if we use useAllowedRoutes(activeProject.id, viewerRoutes) instead of just viewerRoutes
  const allowed = isViewer
    ? viewerRoutes
    : isMonitor
      ? monitorRoutes
      : allowedNormalRoutes.concat(allowedAdminRoutes);
  const firstPath = allowed.length ? allowed[0].path : "";

  if(loading) return <Spinner />;
  if(error) {
    return (
      <PermissionState
        title="Couldn't load your permissions"
        message="Try again, or contact your project admin if this keeps happening."
        action={<Button variant="contained" onClick={() => refetch()}>Retry</Button>}
      />
    );
  }
  if(!firstPath) {
    return (
      <PermissionState
        title="No access available"
        message="You do not have access to any pages for this project."
      />
    );
  }

  return (
    <Loader>
      <Switch>
        {allowed.map((route) => (
          <Route exact sensitive strict key={route.path} {...route} />
        ))}
        <Redirect from="/" to={firstPath} exact strict />
        <Route component={NotFound} />
      </Switch>
    </Loader>
  );
};
